import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { ExportOutlined } from '@ant-design/icons';
import { Divider, Flex, Skeleton, Space, Tag, Typography, theme } from 'antd';
import { authorNames, publicationIssues } from './publicationFacts';
import { PublicationStatusTags } from './PublicationStatusTags';
import { PublicationCoverThumb } from './PublicationCoverThumb';
import type { LineupPublicationLookups } from './useLineupPublications';

const COVER_SIZE = 56;

interface PublicationRowContentProps {
  publicationId: number;
  lookups: LineupPublicationLookups;
  /** Advisory deleted / mod-status tags. */
  showIssues?: boolean;
  /** Consumer-specific diagnostics, shown after a divider. */
  extra?: ReactNode;
}

/** Display face of a publication row: cover, linked title, authors,
 * formats. Consumer-agnostic — any lineup or list can render it. */
export const PublicationRowContent = ({
  publicationId,
  lookups,
  showIssues = true,
  extra,
}: PublicationRowContentProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const publication = lookups.publicationById.get(publicationId);

  if (!publication && lookups.isLoading) {
    return (
      <Skeleton
        active
        avatar={{ shape: 'square', size: COVER_SIZE }}
        title={false}
        paragraph={{ rows: 2, width: ['60%', '40%'] }}
      />
    );
  }

  const authors = authorNames(publication, lookups.authorById).join(', ');
  // Past the skeleton above, an absent publication really is gone —
  // a refetch elsewhere in the lineup must not clear these tags.
  const issues = showIssues ? publicationIssues(publication) : [];

  return (
    <Flex gap={token.marginSM} align="center">
      <PublicationCoverThumb url={publication?.cover_url} size={COVER_SIZE} />

      <Flex vertical style={{ minWidth: 0, flex: 1 }}>
        {/* Flex, not Space: Space's item wrappers cannot shrink, so a long
            title would never ellipsize and would push the link out of view. */}
        <Flex gap={token.marginXXS} align="center">
          <Typography.Text strong ellipsis style={{ minWidth: 0 }}>
            {publication?.title ?? `#${publicationId}`}
          </Typography.Text>
          {/* The link must not count as a double-click on the row. */}
          <Typography.Link
            href={`/user_catalog/publications/${publicationId}`}
            target="_blank"
            rel="noopener noreferrer"
            onDoubleClick={(event) => event.stopPropagation()}
            aria-label={t('user_catalog.common.publications_editor.open')}
            style={{ flexShrink: 0 }}
          >
            <ExportOutlined />
          </Typography.Link>
        </Flex>

        {authors && (
          <Typography.Text type="secondary" ellipsis>
            {authors}
          </Typography.Text>
        )}

        <Space
          size={token.marginXXS}
          wrap
          style={{ marginTop: token.marginXXS }}
        >
          {issues.includes('deleted') && (
            <Tag color="red" style={{ marginInlineEnd: 0 }}>
              {t('user_catalog.common.publications_editor.deleted')}
            </Tag>
          )}

          {publication && <PublicationStatusTags publication={publication} />}

          {extra && (
            <>
              <Divider
                type="vertical"
                style={{
                  margin: 0,
                  height: token.fontSizeHeading5,
                  borderColor: token.colorTextTertiary,
                }}
              />
              {extra}
            </>
          )}
        </Space>
      </Flex>
    </Flex>
  );
};
